import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FileConstants, IconDefinitionConstants } from "utils/constants";
import { SupportFormTypes } from "models/formTypes";
import { SupportIssueCreateFormProps } from "models/propTypes/supportPropTypes";
import {
  FileUploaderProps,
  MultiSearchInputProps,
  SelectInputItemProps,
  SelectInputProps,
  ChoiceProps,
  TextInputProps,
} from "models/propTypes/wintFormPropsTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { FileEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  WintForm,
  TextAreaInput,
  TextInput,
  FileUploader,
  VerticalFormField,
  useWintForm,
  SelectInput,
  Choice,
  MultiSearchInput,
} from "components/common/wintForms";
//#endregion
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const SupportTicketCreationForm: ForwardRefRenderFunction<FormHandles, SupportIssueCreateFormProps> = (props, ref) => {
  const { categorySelectionNotAllowed, agencyMode, agencyName, recipientOptions } = props;
  const supportCategories = useSelector((store: AppState) => store.common.supportCategories);

  const { wintFormProps, connect } = useWintForm<SupportFormTypes.Create>({
    formRef: ref,
    defaultValues: props.defaultValues,
    validationSchema: props.validationSchema,
    handleSubmit: props.handleSubmit,
    onFormUpdate: props.onFormUpdate,
  });

  const messageConnect = connect("message");
  React.useEffect(() => {
    if (!UtilsHelper.isEmpty(messageConnect.value)) messageConnect.setTouched(true);
  }, [props.defaultValues.message]);

  const recipientConnect = connect("recipient");

  // A value added to the server enum ahead of a Web release gets a generic label rather than being
  // dropped: dropping it could leave no field on screen while the schema still requires one.
  const getRecipientLabel = (recipient: WintSuperkollAPIModel.SupportIssueRecipient): string => {
    switch (recipient) {
      case WintSuperkollAPIModel.SupportIssueRecipient.Agency:
        return agencyName
          ? LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SupportIssue.RecipientOptionAgency,
              undefined,
              {
                agencyName: agencyName,
              }
            )
          : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.RecipientOptionAgencyFallback);
      case WintSuperkollAPIModel.SupportIssueRecipient.Wint:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.RecipientOptionWint);
      case WintSuperkollAPIModel.SupportIssueRecipient.Customer:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.RecipientOptionCustomer);
      default:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.RecipientOptionOther);
    }
  };

  // Rendered in the order the server sent them. Never reordered and never filtered on role —
  // recipientOptions is the whole permission answer.
  const recipientItems = recipientOptions.map(
    (recipient): SelectionItem => ({ id: recipient, value: getRecipientLabel(recipient) })
  );

  // A customer on a Byråsupport company has no counterpart to choose: their support goes to their
  // byrå (Hugo 2026-09-10), so the server sends them exactly one recipient. With nothing to decide
  // there is no field to render — but the value still has to reach the server, and the schema still
  // requires it, so the single option is applied for them. This is the one case where the recipient
  // is defaulted; wherever the caller genuinely has a choice (the byrå side, picking between its
  // customer and Wint) nothing is pre-selected, which is what acceptance criterion "inget förval"
  // asks for.
  const onlyRecipient = recipientItems.length === 1 ? recipientItems[0].id : undefined;
  React.useEffect(() => {
    if (onlyRecipient !== undefined && recipientConnect.value === undefined) {
      recipientConnect.setValue(onlyRecipient);
    }
  }, [onlyRecipient]);

  const showPersonSelection = recipientConnect.value === WintSuperkollAPIModel.SupportIssueRecipient.Customer;

  const renderRecipientSelection = () => (
    <>
      {recipientItems.length > 1 ? (
        <VerticalFormField<ChoiceProps>
          Component={Choice}
          {...recipientConnect}
          showMainLabel
          showErrorState
          hideMessageBubble
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.RecipientFieldHeader)}
          items={recipientItems}
        />
      ) : (
        <></>
      )}
      {showPersonSelection ? (
        <VerticalFormField<MultiSearchInputProps>
          Component={MultiSearchInput}
          {...connect("persons")}
          showMainLabel
          label={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SupportIssue.RecipientPersonsFieldHeader
          )}
          placeholder={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SupportIssue.RecipientPersonsPlaceholder
          )}
          items={props.searchPersons}
          suggestedItems={props.suggestedPersons}
        />
      ) : (
        <></>
      )}
    </>
  );

  const renderCategorySelection = () =>
    !categorySelectionNotAllowed ? (
      <VerticalFormField<SelectInputProps>
        Component={SelectInput}
        {...connect("category")}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.CategoryFieldHeader)}
        icon={IconDefinitionConstants.WHashtag}
        items={[...supportCategories]
          .sort((a, b) => {
            if (a.name === "Annat") return 1;
            if (b.name === "Annat") return -1;
            return 0;
          })
          .map(
            (category): SelectInputItemProps => ({
              value: category.id,
              label: category.name ?? "",
            })
          )}
      />
    ) : (
      <></>
    );

  return (
    <WintForm {...wintFormProps}>
      {agencyMode ? renderRecipientSelection() : renderCategorySelection()}
      <VerticalFormField<TextInputProps>
        Component={TextInput}
        {...connect("subject")}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Subject)}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.SubjectHelpText)}
        icon={IconDefinitionConstants.WTag}
        maxLength={512}
      />
      <VerticalFormField<TextInputProps>
        Component={TextAreaInput}
        {...messageConnect}
        minRows={2}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Message)}
        icon={IconDefinitionConstants.WCommentDots}
      />
      <VerticalFormField<FileUploaderProps>
        Component={FileUploader}
        allowedTypes={props.attachmentTypes}
        allowAnyImageTypes
        {...connect("attachments")}
        filePreviewElementId={FileConstants.DialogFilePreviewPortalId}
      />
    </WintForm>
  );
};

export default forwardRef(SupportTicketCreationForm);
